import { toast } from '@britishcouncil/react-admin';
import { ServerError } from 'core';
import { useCallback } from 'react';
import { parseServerErrors } from 'utils';

type MutationFunction<TArgs, TResult> = (args: TArgs) => {
  unwrap(): Promise<TResult>;
};

type UseSafeMutationOptions<TResult> = {
  successToastMessage?: string;
  onSuccess?: (result: TResult) => void | Promise<void>;
  onError?: (errors: ServerError[]) => void;
};

export const useSafeMutation = <TArgs, TResult = unknown>(
  mutationFn: MutationFunction<TArgs, TResult>,
  options: UseSafeMutationOptions<TResult> = {}
) => {
  const { onSuccess, onError, successToastMessage } = options;

  const execute = useCallback(
    async (args: TArgs) => {
      try {
        const result = await mutationFn(args).unwrap();
        await onSuccess?.(result);

        if (successToastMessage) {
          toast({
            type: 'success',
            title: 'Success',
            message: successToastMessage,
          });
        }
        return result;
      } catch (error) {
        onError?.(parseServerErrors(error));
      }
    },
    [mutationFn, onSuccess, onError, successToastMessage]
  );

  return { execute };
};
